import './coapplicantEditEmploymentPage.scss';

import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import red from '@mui/material/colors/red';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import TextField from '@mui/material/TextField';
import { employedTypes } from '@thesavingsgroup/constants/employedTypes';
import { EmploymentType } from '@thesavingsgroup/enums/EmploymentType';
import { isEmpty, toString, uniqueId } from 'lodash';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { inputEventHandler } from '../../../components/utils/inputEventHandler';
import { employedTypeOptions } from '../../../store/applicantStore/constants/employedTypeOptions';
import { employmentStatusOptions } from '../../../store/applicantStore/constants/employmentStatusOptions';
import { HALF_YEAR_IN_MONTHS_AS_STRING } from '../../../store/applicantStore/constants/halfYear';
import { monthsAtIncomeOptions } from '../../../store/applicantStore/constants/monthsAtIncomeOptions';
import { previousEmploymentTypeOptions } from '../../../store/applicantStore/constants/previousEmploymentTypeOptions';
import { unemployedTypeOptions } from '../../../store/applicantStore/constants/unemployedTypeOptions';
import { YES } from '../../../store/applicantStore/types/YesOrNo';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { setDrawerOpen } = useNavigationPaneStore();

  const {
    computed: {
      coapplicantEmployedGrossAnnualIncome,
      coapplicantUnemployedGrossAnnualIncome,
      coapplicantReviewEditPathname,
      coapplicantReviewEditProgress,
    },
    coapplicantCurrentIncomeEmployerName,
    coapplicantCurrentIncomeEmploymentType,
    coapplicantCurrentIncomeJobTitle,
    coapplicantCurrentIncomeMonthsAt,
    coapplicantPreviousIncomeEmployerName,
    coapplicantPreviousIncomeEmploymentType,
    coapplicantPreviousIncomeGrossAnnual,
    coapplicantPreviousIncomeJobTitle,
    isCoapplicantCurrentlyEmployed,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantCurrentIncomeEmployerName,
    setCoapplicantCurrentIncomeEmploymentType,
    setCoapplicantCurrentIncomeGrossAnnual,
    setCoapplicantCurrentIncomeJobTitle,
    setCoapplicantCurrentIncomeMonthsAt,
    setCoapplicantPreviousIncomeEmployerName,
    setCoapplicantPreviousIncomeEmploymentType,
    setCoapplicantPreviousIncomeGrossAnnual,
    setCoapplicantPreviousIncomeJobTitle,
    setIsCoapplicantCurrentlyEmployed,
    syncCoapplicant,
  } = useCoapplicantStore();

  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm({
    criteriaMode: 'firstError',
    defaultValues: {
      currentEmployerNameValue: coapplicantCurrentIncomeEmployerName,
      currentIncomeMonthsAtValue: coapplicantCurrentIncomeMonthsAt,
      employmentTypeValue: coapplicantCurrentIncomeEmploymentType,
      grossAnnualIncomeValue: inputEventHandler.currency.formatter(
        coapplicantEmployedGrossAnnualIncome,
      ),
      isCurrentlyEmployedValue: isCoapplicantCurrentlyEmployed,
      jobTitleValue: coapplicantCurrentIncomeJobTitle,
      previousEmployerNameValue: coapplicantPreviousIncomeEmployerName,
      previousEmploymentTypeValue: coapplicantPreviousIncomeEmploymentType,
      previousGrossAnnualIncomeValue: inputEventHandler.currency.formatter(
        coapplicantPreviousIncomeGrossAnnual,
      ),
      previousJobTitleValue: coapplicantPreviousIncomeJobTitle,
      unemploymentGrossAnnualIncomeValue: inputEventHandler.currency.formatter(
        coapplicantUnemployedGrossAnnualIncome,
      ),
      unemploymentTypeValue: coapplicantCurrentIncomeEmploymentType,
    },
    delayError: 200,
    mode: 'onBlur',
    shouldFocusError: false,
  });

  const shouldShowEmployedTypeFields =
    watch('isCurrentlyEmployedValue', isCoapplicantCurrentlyEmployed) === YES;

  const shouldShowPreviousEmployedTypeFields = employedTypes.includes(
    watch(
      'previousEmploymentTypeValue',
      coapplicantPreviousIncomeEmploymentType,
    ) as EmploymentType,
  );

  const shouldShowPreviousEmploymentSection =
    watch('currentIncomeMonthsAtValue', coapplicantCurrentIncomeMonthsAt) ===
    HALF_YEAR_IN_MONTHS_AS_STRING;

  const employmentStatusValidation = {
    required: t('validation.employmentStatus.required'),
  };

  const employmentTypeValidation = {
    required: t('validation.employmentType.required'),
  };

  const employerNameValidation = {
    required: t('validation.employerName.required'),
  };

  const jobTitleValidation = {
    required: t('validation.jobTitle.required'),
  };

  const monthsAtIncomeValidation = {
    required: t('validation.employmentDuration.required'),
  };

  const grossAnnualIncomeValidation = {
    setValueAs: (value: string): string =>
      inputEventHandler.currency.parser(value),
    required: t('validation.grossAnnualIncome.required'),
    min: {
      value: 0,
      message: t('validation.grossAnnualIncome.min', { min: '$0' }),
    },
  };

  const finishEditing = (): void => {
    navigate(coapplicantReviewEditPathname);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      currentEmployerNameValue,
      currentIncomeMonthsAtValue,
      employmentTypeValue,
      grossAnnualIncomeValue,
      isCurrentlyEmployedValue,
      jobTitleValue,
      previousEmployerNameValue,
      previousEmploymentTypeValue,
      previousGrossAnnualIncomeValue,
      previousJobTitleValue,
      unemploymentGrossAnnualIncomeValue,
      unemploymentTypeValue,
    } = values;

    if (
      !isCurrentlyEmployedValue ||
      (shouldShowEmployedTypeFields && !employmentTypeValue) ||
      (!shouldShowEmployedTypeFields && !unemploymentTypeValue) ||
      (shouldShowEmployedTypeFields && !currentEmployerNameValue) ||
      (shouldShowEmployedTypeFields && !jobTitleValue) ||
      (shouldShowEmployedTypeFields && !grossAnnualIncomeValue) ||
      (!shouldShowEmployedTypeFields && !unemploymentGrossAnnualIncomeValue) ||
      !currentIncomeMonthsAtValue
    ) {
      return;
    }

    if (
      shouldShowPreviousEmploymentSection &&
      (!previousEmploymentTypeValue ||
        (shouldShowPreviousEmployedTypeFields && !previousEmployerNameValue) ||
        (shouldShowPreviousEmployedTypeFields && !previousJobTitleValue) ||
        !previousGrossAnnualIncomeValue)
    ) {
      return;
    }

    setCoapplicantCurrentIncomeMonthsAt(currentIncomeMonthsAtValue);
    setIsCoapplicantCurrentlyEmployed(isCurrentlyEmployedValue);

    if (shouldShowEmployedTypeFields) {
      setCoapplicantCurrentIncomeEmployerName(currentEmployerNameValue);
      setCoapplicantCurrentIncomeEmploymentType(employmentTypeValue);
      setCoapplicantCurrentIncomeGrossAnnual(grossAnnualIncomeValue);
      setCoapplicantCurrentIncomeJobTitle(jobTitleValue);
    } else {
      setCoapplicantCurrentIncomeEmployerName(null);
      setCoapplicantCurrentIncomeEmploymentType(unemploymentTypeValue);
      setCoapplicantCurrentIncomeGrossAnnual(
        unemploymentGrossAnnualIncomeValue,
      );
      setCoapplicantCurrentIncomeJobTitle(null);
    }

    if (shouldShowPreviousEmploymentSection) {
      setCoapplicantPreviousIncomeEmploymentType(previousEmploymentTypeValue);
      setCoapplicantPreviousIncomeGrossAnnual(previousGrossAnnualIncomeValue);
    } else {
      setCoapplicantPreviousIncomeEmploymentType(null);
      setCoapplicantPreviousIncomeGrossAnnual(null);
    }

    if (
      shouldShowPreviousEmploymentSection &&
      shouldShowPreviousEmployedTypeFields
    ) {
      setCoapplicantPreviousIncomeEmployerName(previousEmployerNameValue);
      setCoapplicantPreviousIncomeJobTitle(previousJobTitleValue);
    } else {
      setCoapplicantPreviousIncomeEmployerName(null);
      setCoapplicantPreviousIncomeJobTitle(null);
    }

    if (isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        finishEditing();
      }

      return;
    }

    finishEditing();
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="coapplicant-edit-employment-page" sx={theme.pageLayout}>
        <header
          data-testid={locator.pages.coapplicantEditEmployment.header.root}
        >
          <button
            aria-label="Previous step"
            data-testid={
              locator.pages.coapplicantEditEmployment.header.previousStepButton
            }
            type="button"
            onClick={finishEditing}
          >
            <KeyboardBackspaceRoundedIcon />
          </button>
          <button
            aria-label="Toggle drawer"
            data-testid={
              locator.pages.coapplicantEditEmployment.header.drawerButton
            }
            type="button"
            onClick={() => {
              setDrawerOpen(true);
            }}
          >
            <MenuRoundedIcon />
          </button>
        </header>

        <div className="progress-container" role="complementary">
          <LinearProgress
            aria-label="Application completed percentage"
            data-testid={locator.pages.coapplicantEditEmployment.progress.root}
            value={coapplicantReviewEditProgress}
            variant="determinate"
          />
        </div>

        <main>
          <form
            data-testid={locator.pages.coapplicantEditEmployment.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.coapplicantEditEmployment.form.h1}>
                {t('CoapplicantEditEmploymentPage.title')}
              </h1>
              <h2 data-testid={locator.pages.coapplicantEditEmployment.form.h2}>
                {t('CoapplicantEditEmploymentPage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantEditEmployment.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <TextField
                    {...register(
                      'isCurrentlyEmployedValue',
                      employmentStatusValidation,
                    )}
                    SelectProps={{
                      IconComponent: ExpandMoreRoundedIcon,
                    }}
                    error={!isEmpty(errors.isCurrentlyEmployedValue)}
                    inputProps={{
                      'data-testid':
                        locator.pages.coapplicantEditEmployment.form
                          .employmentStatusSelect,
                    }}
                    label={t('CoapplicantEditEmploymentPage.employmentStatus')}
                    value={watch(
                      'isCurrentlyEmployedValue',
                      isCoapplicantCurrentlyEmployed,
                    )}
                    fullWidth
                    select
                  >
                    <MenuItem value="" disabled>
                      {t('CoapplicantEditEmploymentPage.employmentStatus')}
                    </MenuItem>

                    {employmentStatusOptions.map(({ name, value }) => {
                      return (
                        <MenuItem key={uniqueId(value)} value={value}>
                          {name}
                        </MenuItem>
                      );
                    })}
                  </TextField>

                  {!isEmpty(errors.isCurrentlyEmployedValue) && (
                    <span
                      data-testid={
                        locator.pages.coapplicantEditEmployment.form
                          .employmentStatusSelectError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.isCurrentlyEmployedValue.message)}
                    </span>
                  )}
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  {shouldShowEmployedTypeFields && (
                    <div>
                      <TextField
                        {...register(
                          'employmentTypeValue',
                          employmentTypeValidation,
                        )}
                        SelectProps={{
                          IconComponent: ExpandMoreRoundedIcon,
                        }}
                        error={!isEmpty(errors.employmentTypeValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.coapplicantEditEmployment.form
                              .employmentTypeSelect,
                        }}
                        label={t('CoapplicantEditEmploymentPage.type')}
                        value={watch(
                          'employmentTypeValue',
                          coapplicantCurrentIncomeEmploymentType,
                        )}
                        fullWidth
                        select
                      >
                        <MenuItem value="" disabled>
                          {t('CoapplicantEditEmploymentPage.type')}
                        </MenuItem>

                        {employedTypeOptions.map(({ name, value }) => {
                          return (
                            <MenuItem key={uniqueId(value)} value={value}>
                              {name}
                            </MenuItem>
                          );
                        })}
                      </TextField>

                      {!isEmpty(errors.employmentTypeValue) && (
                        <span
                          data-testid={
                            locator.pages.coapplicantEditEmployment.form
                              .employmentTypeSelectError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.employmentTypeValue.message)}
                        </span>
                      )}
                    </div>
                  )}

                  {!shouldShowEmployedTypeFields && (
                    <div>
                      <TextField
                        {...register(
                          'unemploymentTypeValue',
                          employmentTypeValidation,
                        )}
                        SelectProps={{
                          IconComponent: ExpandMoreRoundedIcon,
                        }}
                        error={!isEmpty(errors.unemploymentTypeValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.coapplicantEditEmployment.form
                              .unemploymentTypeSelect,
                        }}
                        label={t('CoapplicantEditEmploymentPage.type')}
                        value={watch(
                          'unemploymentTypeValue',
                          coapplicantCurrentIncomeEmploymentType,
                        )}
                        fullWidth
                        select
                      >
                        <MenuItem value="" disabled>
                          {t('CoapplicantEditEmploymentPage.type')}
                        </MenuItem>

                        {unemployedTypeOptions.map(({ name, value }) => {
                          return (
                            <MenuItem key={uniqueId(value)} value={value}>
                              {name}
                            </MenuItem>
                          );
                        })}
                      </TextField>

                      {!isEmpty(errors.unemploymentTypeValue) && (
                        <span
                          data-testid={
                            locator.pages.coapplicantEditEmployment.form
                              .unemploymentTypeSelectError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.unemploymentTypeValue.message)}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {shouldShowEmployedTypeFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register(
                        'currentEmployerNameValue',
                        employerNameValidation,
                      )}
                      error={!isEmpty(errors.currentEmployerNameValue)}
                      inputProps={{
                        'data-testid':
                          locator.pages.coapplicantEditEmployment.form
                            .employerNameInput,
                      }}
                      label={t('CoapplicantEditEmploymentPage.employerName')}
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.currentEmployerNameValue) && (
                      <span
                        data-testid={
                          locator.pages.coapplicantEditEmployment.form
                            .employerNameInputError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.currentEmployerNameValue.message)}
                      </span>
                    )}
                  </div>
                )}

                {shouldShowEmployedTypeFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register('jobTitleValue', jobTitleValidation)}
                      error={!isEmpty(errors.jobTitleValue)}
                      inputProps={{
                        'data-testid':
                          locator.pages.coapplicantEditEmployment.form
                            .jobTitleInput,
                      }}
                      label={t('CoapplicantEditEmploymentPage.jobTitle')}
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.jobTitleValue) && (
                      <span
                        data-testid={
                          locator.pages.coapplicantEditEmployment.form
                            .jobTitleInputError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.jobTitleValue.message)}
                      </span>
                    )}
                  </div>
                )}

                <div style={{ marginTop: '1.5rem' }}>
                  {shouldShowEmployedTypeFields && (
                    <div>
                      <TextField
                        {...register(
                          'grossAnnualIncomeValue',
                          grossAnnualIncomeValidation,
                        )}
                        error={!isEmpty(errors.grossAnnualIncomeValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.coapplicantEditEmployment.form
                              .grossAnnualIncomeInput,
                          inputMode: 'numeric',
                        }}
                        label={t(
                          'CoapplicantEditEmploymentPage.grossAnnualIncome',
                        )}
                        type="text"
                        variant="outlined"
                        fullWidth
                        onBlur={inputEventHandler.currency.blur(
                          'grossAnnualIncomeValue',
                          setValue,
                          trigger,
                        )}
                        onChange={inputEventHandler.currency.change(
                          'grossAnnualIncomeValue',
                          setValue,
                        )}
                        onFocus={inputEventHandler.currency.focus(
                          'grossAnnualIncomeValue',
                          setValue,
                        )}
                        onKeyDown={inputEventHandler.number.keydown}
                        onPasteCapture={inputEventHandler.number.pasteCapture}
                      />

                      {!isEmpty(errors.grossAnnualIncomeValue) && (
                        <span
                          data-testid={
                            locator.pages.coapplicantEditEmployment.form
                              .grossAnnualIncomeInputError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.grossAnnualIncomeValue.message)}
                        </span>
                      )}
                    </div>
                  )}

                  {!shouldShowEmployedTypeFields && (
                    <div>
                      <TextField
                        {...register(
                          'unemploymentGrossAnnualIncomeValue',
                          grossAnnualIncomeValidation,
                        )}
                        error={
                          !isEmpty(errors.unemploymentGrossAnnualIncomeValue)
                        }
                        inputProps={{
                          'data-testid':
                            locator.pages.coapplicantEditEmployment.form
                              .unemploymentGrossAnnualIncomeInput,
                          inputMode: 'numeric',
                        }}
                        label={t(
                          'CoapplicantEditEmploymentPage.grossAnnualIncome',
                        )}
                        type="text"
                        variant="outlined"
                        fullWidth
                        onBlur={inputEventHandler.currency.blur(
                          'unemploymentGrossAnnualIncomeValue',
                          setValue,
                          trigger,
                        )}
                        onChange={inputEventHandler.currency.change(
                          'unemploymentGrossAnnualIncomeValue',
                          setValue,
                        )}
                        onFocus={inputEventHandler.currency.focus(
                          'unemploymentGrossAnnualIncomeValue',
                          setValue,
                        )}
                        onKeyDown={inputEventHandler.number.keydown}
                        onPasteCapture={inputEventHandler.number.pasteCapture}
                      />

                      {!isEmpty(errors.unemploymentGrossAnnualIncomeValue) && (
                        <span
                          data-testid={
                            locator.pages.coapplicantEditEmployment.form
                              .unemploymentGrossAnnualIncomeInputError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(
                            errors.unemploymentGrossAnnualIncomeValue.message,
                          )}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    {...register(
                      'currentIncomeMonthsAtValue',
                      monthsAtIncomeValidation,
                    )}
                    SelectProps={{
                      IconComponent: ExpandMoreRoundedIcon,
                    }}
                    error={!isEmpty(errors.currentIncomeMonthsAtValue)}
                    inputProps={{
                      'data-testid':
                        locator.pages.coapplicantEditEmployment.form
                          .employmentDurationSelect,
                    }}
                    label={t('CoapplicantEditEmploymentPage.duration')}
                    value={watch(
                      'currentIncomeMonthsAtValue',
                      coapplicantCurrentIncomeMonthsAt,
                    )}
                    fullWidth
                    select
                  >
                    <MenuItem value="" disabled>
                      {t('CoapplicantEditEmploymentPage.duration')}
                    </MenuItem>

                    {monthsAtIncomeOptions.map(({ name, value }) => {
                      return (
                        <MenuItem key={uniqueId(value)} value={value}>
                          {name}
                        </MenuItem>
                      );
                    })}
                  </TextField>

                  {!isEmpty(errors.currentIncomeMonthsAtValue) && (
                    <span
                      data-testid={
                        locator.pages.coapplicantEditEmployment.form
                          .employmentDurationSelectError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.currentIncomeMonthsAtValue.message)}
                    </span>
                  )}
                </div>
              </div>

              {shouldShowPreviousEmploymentSection && (
                <div
                  data-testid={
                    locator.pages.coapplicantEditEmployment.form
                      .previousEmploymentRoot
                  }
                  style={{ marginBottom: '3rem' }}
                >
                  <h3
                    data-testid={
                      locator.pages.coapplicantEditEmployment.form
                        .previousEmploymentH3
                    }
                  >
                    {t('CoapplicantEditEmploymentPage.previousEmployment')}
                  </h3>

                  <div>
                    <TextField
                      {...register(
                        'previousEmploymentTypeValue',
                        employmentTypeValidation,
                      )}
                      SelectProps={{
                        IconComponent: ExpandMoreRoundedIcon,
                      }}
                      autoComplete="address-level1"
                      error={!isEmpty(errors.previousEmploymentTypeValue)}
                      inputProps={{
                        'data-testid':
                          locator.pages.coapplicantEditEmployment.form
                            .previousEmploymentTypeSelect,
                      }}
                      label={t('CoapplicantEditEmploymentPage.type')}
                      value={watch(
                        'previousEmploymentTypeValue',
                        coapplicantPreviousIncomeEmploymentType,
                      )}
                      fullWidth
                      select
                    >
                      <MenuItem value="" disabled>
                        {t('CoapplicantEditEmploymentPage.type')}
                      </MenuItem>

                      {previousEmploymentTypeOptions.map(({ name, value }) => {
                        return (
                          <MenuItem key={value} value={value}>
                            {name}
                          </MenuItem>
                        );
                      })}
                    </TextField>

                    {!isEmpty(errors.previousEmploymentTypeValue) && (
                      <span
                        data-testid={
                          locator.pages.coapplicantEditEmployment.form
                            .previousEmploymentTypeSelectError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.previousEmploymentTypeValue.message)}
                      </span>
                    )}
                  </div>

                  {shouldShowPreviousEmployedTypeFields && (
                    <div style={{ marginTop: '1.5rem' }}>
                      <TextField
                        {...register(
                          'previousEmployerNameValue',
                          employerNameValidation,
                        )}
                        error={!isEmpty(errors.previousEmployerNameValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.coapplicantEditEmployment.form
                              .previousEmployerNameInput,
                        }}
                        label={t('CoapplicantEditEmploymentPage.employerName')}
                        variant="outlined"
                        fullWidth
                      />

                      {!isEmpty(errors.previousEmployerNameValue) && (
                        <span
                          data-testid={
                            locator.pages.coapplicantEditEmployment.form
                              .previousEmployerNameInputError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.previousEmployerNameValue.message)}
                        </span>
                      )}
                    </div>
                  )}

                  {shouldShowPreviousEmployedTypeFields && (
                    <div style={{ marginTop: '1.5rem' }}>
                      <TextField
                        {...register(
                          'previousJobTitleValue',
                          jobTitleValidation,
                        )}
                        error={!isEmpty(errors.previousJobTitleValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.coapplicantEditEmployment.form
                              .previousEmploymentJobTitleInput,
                        }}
                        label={t('CoapplicantEditEmploymentPage.jobTitle')}
                        variant="outlined"
                        fullWidth
                      />

                      {!isEmpty(errors.previousJobTitleValue) && (
                        <span
                          data-testid={
                            locator.pages.coapplicantEditEmployment.form
                              .previousEmploymentJobTitleInputError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.previousJobTitleValue.message)}
                        </span>
                      )}
                    </div>
                  )}

                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register(
                        'previousGrossAnnualIncomeValue',
                        grossAnnualIncomeValidation,
                      )}
                      error={!isEmpty(errors.previousGrossAnnualIncomeValue)}
                      inputProps={{
                        'data-testid':
                          locator.pages.coapplicantEditEmployment.form
                            .previousEmploymentGrossAnnualIncomeInput,
                        inputMode: 'numeric',
                      }}
                      label={t(
                        'CoapplicantEditEmploymentPage.grossAnnualIncome',
                      )}
                      type="text"
                      variant="outlined"
                      fullWidth
                      onBlur={inputEventHandler.currency.blur(
                        'previousGrossAnnualIncomeValue',
                        setValue,
                        trigger,
                      )}
                      onChange={inputEventHandler.currency.change(
                        'previousGrossAnnualIncomeValue',
                        setValue,
                      )}
                      onFocus={inputEventHandler.currency.focus(
                        'previousGrossAnnualIncomeValue',
                        setValue,
                      )}
                      onKeyDown={inputEventHandler.number.keydown}
                      onPasteCapture={inputEventHandler.number.pasteCapture}
                    />

                    {!isEmpty(errors.previousGrossAnnualIncomeValue) && (
                      <span
                        data-testid={
                          locator.pages.coapplicantEditEmployment.form
                            .previousEmploymentGrossAnnualIncomeInputError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(
                          errors.previousGrossAnnualIncomeValue.message,
                        )}
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div className="inline-buttons">
                  <LoadingButton
                    color="secondary"
                    data-testid={
                      locator.pages.coapplicantEditEmployment.form.cancelButton
                    }
                    loadingPosition="start"
                    startIcon={<></>}
                    type="button"
                    variant="contained"
                    fullWidth
                    onClick={finishEditing}
                  >
                    {t('CoapplicantEditEmploymentPage.cancel')}
                  </LoadingButton>

                  <LoadingButton
                    color="primary"
                    data-testid={
                      locator.pages.coapplicantEditEmployment.form.submitButton
                    }
                    disabled={!isValid}
                    loading={isSyncingCoapplicant}
                    loadingIndicator={t('CoapplicantEditEmploymentPage.saving')}
                    startIcon={<></>}
                    type="submit"
                    variant="contained"
                    fullWidth
                  >
                    {t('CoapplicantEditEmploymentPage.save')}
                  </LoadingButton>
                </div>
              </div>
            </div>
            <footer
              data-testid={locator.pages.coapplicantEditEmployment.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantEditEmploymentPage';
